import { Button, CircularProgress, Grid, TextField, Tooltip } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { FC, Fragment, useState } from 'react';
import { CustomTypography } from 'shared/elements/CustomTypography';
import { ICarFormsProps, ICarVarianItem } from 'shared/interfaces/ICars';
import { useStyles } from 'pages/MyInfo/MyInfo.styles';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';

interface CarFormComponent {
  carForms: ICarFormsProps[];
  onSave?: VoidFunction;
  disableSaveButton?: boolean;
  imageLength: number;
}

const fieldType = {
  vehicle: 'Vehicle Type',
  variant: 'Variant',
};

const AutoCompleteForm = ({ form }: { form: ICarFormsProps }) => {
  const classes = useStyles();

  const [uiValue, setUiValue] = useState('');

  const handleOnInputChange = (event: React.ChangeEvent<{}>, newInputValue: string) => {
    setUiValue(newInputValue);
  };

  return (
    <Autocomplete
      value={form.value}
      noOptionsText={form?.noOptionText || 'No Options'}
      popupIcon={
        form?.isLoading && form?.list?.length === 0 ? (
          <CircularProgress size={12} style={{ marginTop: 5 }} />
        ) : (
          <ArrowDropDownIcon />
        )
      }
      options={form?.list}
      getOptionLabel={(option) =>
        form?.name === fieldType.variant ? `${option.name} (${option?.year})` : option?.name
      }
      onInputChange={handleOnInputChange}
      getOptionSelected={(option, value) =>
        form?.name === fieldType.variant
          ? `${option?.year} ${option?.name} ${option?.description}` ===
            `${value?.year} ${value?.name} ${value?.description}`
          : option?.name === value?.name
      }
      filterOptions={(options, state) => {
        const { inputValue } = state;
        return options.filter((option) =>
          `${option?.year} ${option.name} ${option?.description}`.toLowerCase().includes(inputValue.toLowerCase()),
        );
      }}
      classes={{
        clearIndicator: uiValue !== '' ? classes.autoCompleteClearIndicator : undefined,
      }}
      onChange={(event, newValue) => form.handleOnChange(newValue as ICarVarianItem)}
      disabled={form?.disable}
      renderOption={(option) => (
        <li style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
          {form?.name === fieldType.variant ? (
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <p>{`${option.name}`}</p>
                <CustomTypography style={{ fontStyle: 'italic', fontSize: 10 }}>{option?.description}</CustomTypography>
              </div>
              <p>({option.year})</p>
            </div>
          ) : (
            <li>{option?.name}</li>
          )}
          {form?.isLoading && option?.id === form?.list[form?.list?.length - 1]?.id ? (
            <div style={{ textAlign: 'center', marginTop: 5 }}>
              <CircularProgress size={16} />
            </div>
          ) : (
            <Fragment />
          )}
        </li>
      )}
      renderInput={(params) => (
        <>
          <TextField
            placeholder={form?.placeholder || 'Select'}
            {...params}
            variant="outlined"
            inputProps={{
              ...params.inputProps,
              readOnly: form?.readOnly,
            }}
          />
        </>
      )}
      ListboxProps={{
        onScroll: form?.onScroll,
      }}
    />
  );
};

const CarForm: FC<CarFormComponent> = ({ carForms, onSave, disableSaveButton, imageLength }) => {
  const classes = useStyles();
  return (
    <Grid container>
      {carForms?.map((form) => {
        return (
          <Grid item xs={12} key={form?.name}>
            <CustomTypography color="mainGray" fontSize={14}>
              {form.name}
            </CustomTypography>

            {form.name !== fieldType.vehicle ? (
              <AutoCompleteForm form={form} />
            ) : (
              <TextField
                key={form.name}
                placeholder={form?.placeholder || 'Select'}
                variant="outlined"
                value={form?.isStringForm?.value}
                fullWidth
                inputProps={{
                  readOnly: true,
                }}
                className={classes.removeOutline}
                disabled={form?.disable}
              />
            )}
          </Grid>
        );
      })}

      <Grid item xs={12} style={{ marginTop: 5 }}>
        <Tooltip title={`Maximum of 5 image uploads only`} disableHoverListener={imageLength <= 5}>
          <div>
            <Button
              onClick={onSave}
              disabled={disableSaveButton}
              variant="contained"
              color="primary"
              fullWidth
              size="medium"
              style={{ height: '50px', fontSize: 16 }}
            >
              Save
            </Button>
          </div>
        </Tooltip>
      </Grid>
    </Grid>
  );
};

export default CarForm;
